/**
 * SoftphoneContext — owns the SIP.js UserAgent + registration + call state.
 *
 * Mounted ONCE near the top of the authenticated tree (in App.js) so that:
 *   - The /softphone page (full dial-pad UI) and the FloatingDialer
 *     (mini-phone overlay in Layout) share the same UA — there is exactly
 *     one PJSIP registration per browser tab.
 *   - Click-to-call from anywhere (Phonebook, mini-dialer, ?dial= URL,
 *     BroadcastChannel from other tabs) routes through the same `placeCall`.
 *   - Remote audio plays continuously across route changes (the <audio>
 *     element lives in this provider, not in the /softphone page).
 *
 * The provider also auto-registers on first credential fetch and listens
 * on BroadcastChannel('voice-gateway-dial') for cross-tab dial requests.
 */
import React, {
    createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
    UserAgent, Inviter, Registerer, SessionState, RegistererState,
} from 'sip.js';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { DIAL_CHANNEL, normalizeNumber } from '@/lib/dialer';

const SoftphoneContext = createContext(null);

// Re-export so consumers don't need to import sip.js directly
export { SessionState };

export const SoftphoneProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [creds, setCreds] = useState(null);
    const [credsError, setCredsError] = useState(null);
    const [phase, setPhase] = useState('idle');
    const [dest, setDest] = useState('');
    const [muted, setMuted] = useState(false);
    const [held, setHeld] = useState(false);
    const [duration, setDuration] = useState(0);
    const [remote, setRemote] = useState('');
    const [pendingDial, setPendingDial] = useState(null);
    const [pendingDialName, setPendingDialName] = useState(null);
    // Incoming-call notification visibility (FloatingDialer uses this)
    const [hasIncoming, setHasIncoming] = useState(false);

    const uaRef = useRef(null);
    const regRef = useRef(null);
    const sessionRef = useRef(null);
    const audioRef = useRef(null);
    const durTimer = useRef(null);
    const credsLoadedRef = useRef(false);

    // ---- Fetch creds for logged-in user ----------------------------------
    useEffect(() => {
        if (!token || !user) {
            setCreds(null);
            setCredsError(null);
            credsLoadedRef.current = false;
            return;
        }
        if (credsLoadedRef.current) return;
        credsLoadedRef.current = true;
        api.get('/softphone/me')
            .then((r) => setCreds(r.data))
            .catch((e) => setCredsError(e.response?.data?.detail || 'Klapka není přiřazena.'));
    }, [token, user]);

    // ---- Helpers ---------------------------------------------------------
    const cleanupSession = useCallback(() => {
        if (durTimer.current) clearInterval(durTimer.current);
        durTimer.current = null;
        setDuration(0);
        setRemote('');
        setHeld(false);
        setMuted(false);
        setHasIncoming(false);
        sessionRef.current = null;
    }, []);

    const attachMedia = useCallback((session) => {
        const sd = session.sessionDescriptionHandler;
        const pc = sd?.peerConnection;
        if (!pc) return;
        const stream = new MediaStream();
        pc.getReceivers().forEach((r) => r.track && stream.addTrack(r.track));
        if (audioRef.current) {
            audioRef.current.srcObject = stream;
            audioRef.current.play().catch(() => { /* user gesture may be required */ });
        }
    }, []);

    // ---- Register / Unregister -------------------------------------------
    const register = useCallback(async () => {
        if (!creds) return;
        if (!creds.wss_url || !creds.sip_domain) {
            toast.error('WebRTC není nakonfigurováno. Nastavte WSS URL a SIP doménu.');
            return;
        }
        if (uaRef.current) return; // already registering / registered
        setPhase('connecting');
        try {
            const uri = UserAgent.makeURI(`sip:${creds.sip_username}@${creds.sip_domain}`);
            if (!uri) throw new Error('Invalid SIP URI');

            const iceServers = [
                ...(creds.stun_servers || []).map((u) => ({ urls: u })),
            ];
            if (creds.turn_url && creds.turn_username && creds.turn_credential) {
                iceServers.push({
                    urls: creds.turn_url,
                    username: creds.turn_username,
                    credential: creds.turn_credential,
                });
            }
            const ua = new UserAgent({
                uri,
                transportOptions: { server: creds.wss_url },
                authorizationUsername: creds.sip_username,
                authorizationPassword: creds.password,
                displayName: creds.display_name,
                sessionDescriptionHandlerFactoryOptions: {
                    peerConnectionConfiguration: { iceServers },
                    constraints: { audio: true, video: !!creds.enable_video },
                },
                delegate: {
                    onInvite: (invitation) => {
                        sessionRef.current = invitation;
                        setRemote(invitation.remoteIdentity?.uri?.user || 'neznámý');
                        setPhase('ringing');
                        setHasIncoming(true);
                        invitation.stateChange.addListener((s) => {
                            if (s === SessionState.Established) {
                                attachMedia(invitation);
                                setHasIncoming(false);
                                setPhase('in_call');
                                durTimer.current = setInterval(
                                    () => setDuration((d) => d + 1), 1000);
                            } else if (s === SessionState.Terminated) {
                                cleanupSession();
                                setPhase('registered');
                            }
                        });
                    },
                },
            });
            uaRef.current = ua;
            await ua.start();
            const reg = new Registerer(ua);
            regRef.current = reg;
            reg.stateChange.addListener((s) => {
                if (s === RegistererState.Registered) setPhase('registered');
                else if (s === RegistererState.Unregistered) setPhase('idle');
            });
            await reg.register();
        } catch (e) {
            console.error(e);
            setPhase('failed');
            uaRef.current = null;
            regRef.current = null;
            toast.error('Registrace selhala: ' + (e.message || 'neznámá chyba'));
        }
    }, [creds, attachMedia, cleanupSession]);

    const unregister = useCallback(async () => {
        try { regRef.current?.unregister(); } catch (_) { /* noop */ }
        try { uaRef.current?.stop(); } catch (_) { /* noop */ }
        regRef.current = null;
        uaRef.current = null;
        setPhase('idle');
    }, []);

    // Cleanup on unmount (logout / app shutdown)
    useEffect(() => () => { unregister(); }, [unregister]);

    // ---- Call control ----------------------------------------------------
    const placeCall = useCallback(async (overrideTarget) => {
        if (!uaRef.current || !creds) return;
        const target = (overrideTarget != null ? String(overrideTarget) : dest).trim();
        if (!target) {
            toast.error('Zadejte číslo k vytočení.');
            return;
        }
        setDest(target);
        const targetUri = UserAgent.makeURI(`sip:${target}@${creds.sip_domain}`);
        if (!targetUri) {
            toast.error('Neplatný cíl.');
            return;
        }
        const inviter = new Inviter(uaRef.current, targetUri, {
            sessionDescriptionHandlerOptions: {
                constraints: { audio: true, video: !!creds.enable_video },
            },
        });
        sessionRef.current = inviter;
        setRemote(target);
        setPhase('ringing');
        inviter.stateChange.addListener((s) => {
            if (s === SessionState.Established) {
                attachMedia(inviter);
                setPhase('in_call');
                durTimer.current = setInterval(() => setDuration((d) => d + 1), 1000);
            } else if (s === SessionState.Terminated) {
                cleanupSession();
                setPhase('registered');
            }
        });
        try {
            await inviter.invite();
        } catch (e) {
            toast.error('Volání selhalo: ' + (e.message || 'unknown'));
            cleanupSession();
            setPhase('registered');
        }
    }, [dest, creds, attachMedia, cleanupSession]);

    const hangup = useCallback(async () => {
        const s = sessionRef.current;
        if (!s) return;
        try {
            if (s.state === SessionState.Initial || s.state === SessionState.Establishing) {
                if (s.cancel) await s.cancel();
                else if (s.reject) await s.reject();
            } else if (s.state === SessionState.Established) {
                await s.bye();
            }
        } catch (_) { /* noop */ }
    }, []);

    const acceptCall = useCallback(async () => {
        const s = sessionRef.current;
        if (!s?.accept) return;
        try {
            await s.accept({
                sessionDescriptionHandlerOptions: {
                    constraints: { audio: true, video: !!creds?.enable_video },
                },
            });
            setHasIncoming(false);
        } catch (e) {
            toast.error('Příjem selhal: ' + (e.message || 'unknown'));
        }
    }, [creds]);

    const rejectCall = useCallback(async () => {
        const s = sessionRef.current;
        if (!s?.reject) return;
        try { await s.reject(); } catch (_) { /* noop */ }
        setHasIncoming(false);
    }, []);

    const toggleMute = useCallback(() => {
        const s = sessionRef.current;
        if (!s?.sessionDescriptionHandler) return;
        const pc = s.sessionDescriptionHandler.peerConnection;
        pc?.getSenders().forEach((sender) => {
            if (sender.track?.kind === 'audio') sender.track.enabled = muted;
        });
        setMuted((m) => !m);
    }, [muted]);

    const toggleHold = useCallback(async () => {
        const s = sessionRef.current;
        if (!s?.invite) return;
        try {
            if (!held) {
                await s.invite({ sessionDescriptionHandlerModifiers: [(desc) => {
                    desc.sdp = desc.sdp.replace(/a=sendrecv/g, 'a=sendonly');
                    return Promise.resolve(desc);
                }] });
            } else {
                await s.invite();
            }
            setHeld((h) => !h);
        } catch (_) { /* noop */ }
    }, [held]);

    const sendDtmf = useCallback((digit) => {
        if (phase !== 'in_call') {
            setDest((d) => d + digit);
            return;
        }
        const s = sessionRef.current;
        if (!s?.sessionDescriptionHandler?.sendDtmf) return;
        try { s.sessionDescriptionHandler.sendDtmf(digit); } catch (_) { /* noop */ }
    }, [phase]);

    // ---- Click-to-call: URL ?dial= param (run once on mount) ------------
    useEffect(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            const d = normalizeNumber(sp.get('dial') || '');
            if (d) {
                setPendingDial(d);
                setPendingDialName(sp.get('name') || null);
                setDest(d);
            }
        } catch (_) { /* ignore */ }
    }, []);

    // ---- Click-to-call: BroadcastChannel cross-tab dial ----------------
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return undefined;
        let bc;
        try {
            bc = new BroadcastChannel(DIAL_CHANNEL);
            bc.onmessage = (e) => {
                const msg = e?.data;
                if (!msg || msg.type !== 'dial') return;
                const tgt = normalizeNumber(msg.target);
                if (!tgt) return;
                try { bc.postMessage({ type: 'dial-ack', target: tgt }); } catch (_) { /* noop */ }
                setPendingDial(tgt);
                setPendingDialName(msg.contactName || null);
                setDest(tgt);
                try { window.focus(); } catch (_) { /* ignore */ }
            };
        } catch (_) { /* unsupported */ }
        return () => { try { bc?.close(); } catch (_) { /* ignore */ } };
    }, []);

    // ---- Click-to-call: same-tab CustomEvent (fast path for in-app dial)
    useEffect(() => {
        const onSameTab = (e) => {
            const msg = e?.detail;
            if (!msg || msg.type !== 'dial') return;
            const tgt = normalizeNumber(msg.target);
            if (!tgt) return;
            // Ack via custom event so dialer.triggerCall() knows to skip popup
            try {
                window.dispatchEvent(new CustomEvent(DIAL_CHANNEL + '-ack', {
                    detail: { type: 'dial-ack', target: tgt },
                }));
            } catch (_) { /* noop */ }
            setPendingDial(tgt);
            setPendingDialName(msg.contactName || null);
            setDest(tgt);
        };
        window.addEventListener(DIAL_CHANNEL, onSameTab);
        return () => window.removeEventListener(DIAL_CHANNEL, onSameTab);
    }, []);

    // ---- Auto-register when creds + pendingDial both present -----------
    useEffect(() => {
        if (creds && pendingDial && phase === 'idle') register();
    }, [creds, pendingDial, phase, register]);

    // ---- Auto-dial once registered -------------------------------------
    useEffect(() => {
        if (phase === 'registered' && pendingDial && uaRef.current) {
            const tgt = pendingDial;
            const nm = pendingDialName;
            setPendingDial(null);
            setPendingDialName(null);
            toast.success(nm ? `Vytáčím ${nm} · ${tgt}` : `Vytáčím ${tgt}`);
            setTimeout(() => placeCall(tgt), 50);
        }
    }, [phase, pendingDial, pendingDialName, placeCall]);

    const value = {
        creds, credsError,
        phase, dest, setDest, muted, held, duration, remote, hasIncoming,
        audioRef,
        register, unregister,
        placeCall, hangup, acceptCall, rejectCall,
        toggleMute, toggleHold, sendDtmf,
    };

    return (
        <SoftphoneContext.Provider value={value}>
            {children}
            {/* Single shared remote-audio sink — lives globally so calls stay audible across route changes. */}
            <audio ref={audioRef} autoPlay data-testid="softphone-audio-sink"
                   style={{ display: 'none' }} />
        </SoftphoneContext.Provider>
    );
};

export const useSoftphone = () => {
    const ctx = useContext(SoftphoneContext);
    if (!ctx) throw new Error('useSoftphone must be used within SoftphoneProvider');
    return ctx;
};
